import { ComponentFields, Field, Item } from '@sitecore-jss/sitecore-jss-nextjs';
import { isArray, isEmpty, isNil } from 'lodash';

import { isLinkField } from '@gn-audio/jss-core/sitecore-layout/utilities';

import { DictionaryFields, SitecoreField, SitecoreFieldTypes } from '../types';
import { isDropTree } from './getDropTree';
import { getSharedDictionary } from './getSharedDictionary';
import { isBooleanField } from './getSharedLink';
import { isMediaItem } from './getSharedMediaItem';

export const isItem = (value: Field | Item | Item[]): value is Item => {
  return 'fields' in value;
};

export const isFilledIn = (item: unknown): boolean => {
  if (isNil(item)) {
    return false;
  }
  if (item && typeof item === 'object') {
    return Object.values(item).some((value) => isFilledIn(value));
  }
  if (['number', 'boolean'].includes(typeof item)) {
    return !isNil(item);
  }
  return !isEmpty(item);
};

export const isItemArray = (value: Field | Item | Item[] | any): value is Item[] => {
  const isArray = Array.isArray(value);
  if (!isArray) return false;
  if (value.length === 0) return true;
  const [firstItem] = value;
  return isItem(firstItem);
};

export const mapSharedPropertiesToFields = <ReturnType extends Record<string, unknown>>(
  item: SitecoreFieldTypes | ComponentFields
): ReturnType => {
  const itemKeys = Object.keys(item ?? {});
  const uniqueKeys = Array.from(new Set(itemKeys.map((key) => key.replace('Shared', ''))));

  const newObj = uniqueKeys.reduce<Record<string, SitecoreField | unknown>>((acc, key) => {
    const sharedKey = `${key}Shared`;
    const dictionaryKey = `${key}Dictionary`;
    let field = item[key];
    const sharedField = item[sharedKey] ?? item[dictionaryKey];

    const isLocalValuePresent = typeof field !== 'undefined';

    if (isLocalValuePresent) {
      if (typeof field === 'string') {
        acc[key] = { value: field };
        return acc;
      }
      field = field as Field;
      if (isBooleanField(field)) {
        acc[key] = { value: field.value === 'True' };
        return acc;
      }

      if (typeof field?.value === 'string' && !isEmpty(field.value)) {
        acc[key] = field;
        return acc;
      }

      if (isLinkField(field)) {
        acc[key] = { value: field.value };
        return acc;
      }

      if (isMediaItem(field)) {
        acc[key] = field;
        return acc;
      }

      if (isFilledIn(field?.value)) {
        acc[key] = field;
        return acc;
      }

      if (isDropTree(field)) {
        const fields = mapSharedPropertiesToFields(field.fields);
        acc[key] = { ...fields, id: field.id, url: field.url };
        return acc;
      }

      if (isArray(field) && field.length > 0) {
        acc[key] = field.map((item) => {
          if (isMediaItem(item)) {
            return item;
          } else {
            const fields = mapSharedPropertiesToFields(item.fields);
            return { ...fields, id: item.id };
          }
        });
        return acc;
      }
    }

    const sharedDictionary = getSharedDictionary(
      item as DictionaryFields,
      key as keyof typeof item
    );
    if (typeof sharedField === 'string') {
      console.error(
        `ERROR reading shared field ${sharedKey}. Got only ID instead of value. Probably orphaned reference or indexing issue`
      );
      return acc;
    }

    if (sharedField) {
      if (isItemArray(sharedField)) {
        acc[key] = sharedField.map((item) => {
          if (isMediaItem(item)) {
            return item;
          } else {
            const fields = mapSharedPropertiesToFields(item.fields);
            return { ...fields, id: item.id };
          }
        });
      } else if ('value' in sharedField && isBooleanField(sharedField)) {
        acc[key] = {
          value: sharedField.value === 'True',
        };
      } else if ('value' in sharedField) {
        acc[key] = {
          value: sharedField.value,
        };
      } else if (sharedDictionary && 'value' in sharedDictionary) {
        acc[key] = {
          value: sharedDictionary.value,
        };
      } else if (isDropTree(sharedField)) {
        const fields = mapSharedPropertiesToFields(sharedField.fields);
        acc[key] = { ...fields, id: sharedField.id, url: sharedField.url };
        return acc;
      } else {
        acc[key] = {
          value: '',
        };
      }
    } else {
      acc[key] = field;
    }

    return acc;
  }, {});

  return newObj as ReturnType;
};
